import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';

function SettingsEmployeeDepartments() {
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newDepartment, setNewDepartment] = useState('');

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const response = await apiFetch('/api/custom-lists?list_type=employee_department');
      const data = await response.json();
      setDepartments(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching departments:', error);
      setLoading(false);
    }
  };

  const handleAddDepartment = async () => {
    if (!newDepartment.trim()) {
      alert('Please enter a department name');
      return;
    }

    setSaving(true);
    try {
      const response = await apiFetch('/api/custom-lists', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          list_type: 'employee_department',
          list_value: newDepartment.trim()
        })
      });

      if (response.ok) {
        setNewDepartment('');
        fetchDepartments();
      } else {
        alert('Error adding department');
      }
    } catch (error) {
      console.error('Error adding department:', error);
      alert('Error adding department');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteDepartment = async (id, deptName) => {
    if (!window.confirm(`Delete department "${deptName}"?`)) return;

    try {
      await apiFetch(`/api/custom-lists/${id}`, {
        method: 'DELETE'
      });
      fetchDepartments();
    } catch (error) {
      console.error('Error deleting department:', error);
      alert('Error deleting department');
    }
  };

  const handleReorder = async (id, direction) => {
    const currentIndex = departments.findIndex(d => d.id === id);
    if (currentIndex === -1) return;

    const newOrder = currentIndex + direction;
    if (newOrder < 0 || newOrder >= departments.length) return;

    try {
      await apiFetch(`/api/custom-lists/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ display_order: newOrder })
      });
      fetchDepartments();
    } catch (error) {
      console.error('Error reordering department:', error);
    }
  };

  if (loading) {
    return <div className="text-center py-8 text-gray-600">Loading...</div>;
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <h2 className="text-xl font-semibold text-gray-900">Employee Departments</h2>
        <p className="text-sm text-gray-600 mt-1">
          Manage department options (e.g., Engineering, Sales, Administration, Warehouse)
        </p>
      </div>

      {/* Body */}
      <div className="p-6">
        {/* Add New Department */}
        <div className="mb-6 flex gap-3">
          <input
            type="text"
            value={newDepartment}
            onChange={(e) => setNewDepartment(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddDepartment()}
            placeholder="Enter new department (e.g., Technical Support)"
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
          <button
            onClick={handleAddDepartment}
            disabled={saving || !newDepartment.trim()}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition disabled:opacity-50"
          >
            {saving ? 'Adding...' : '+ Add Department'}
          </button>
        </div>

        {/* Department List */}
        {departments.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No departments defined. Add your first one above.
          </div>
        ) : (
          <div className="space-y-2">
            {departments.map((dept, index) => (
              <div
                key={dept.id}
                className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition"
              >
                <div className="flex items-center space-x-3">
                  <div className="flex flex-col space-y-1">
                    <button
                      onClick={() => handleReorder(dept.id, -1)}
                      disabled={index === 0}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => handleReorder(dept.id, 1)}
                      disabled={index === departments.length - 1}
                      className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </div>
                  <span className="font-medium text-gray-900">{dept.list_value}</span>
                </div>
                <button
                  onClick={() => handleDeleteDepartment(dept.id, dept.list_value)}
                  className="px-3 py-1 text-red-600 hover:bg-red-50 rounded transition text-sm"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="border-t border-gray-200 px-6 py-4 bg-gray-50 rounded-b-xl">
        <p className="text-sm text-gray-600">
          💡 <strong>Tip:</strong> Common departments include Engineering, Sales, Administration,
          Warehouse, Technical Support, Management, Finance, and HR.
        </p>
      </div>
    </div>
  );
}

export default SettingsEmployeeDepartments;
